import { afterNextRender, Directive, ElementRef, inject, Input, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
	selector: '[araTableScrollBody]',
	standalone: true,
})
export class TableScrollBodyDirective implements OnDestroy {
	private readonly el = inject(ElementRef<HTMLElement>);
	private readonly renderer = inject(Renderer2);
	private resizeObserver?: ResizeObserver;
	private mutationObserver?: MutationObserver;
	private _enabled = true;

	@Input() set araTableScrollBody(value: boolean | '') {
		this._enabled = value !== false;
	}

	constructor() {
		afterNextRender(() => {
			this.computeMaxHeight();
			this.resizeObserver = new ResizeObserver(() => this.computeMaxHeight());
			const el = this.el.nativeElement;
			// Observe the card (catches static siblings resizing)
			const card = el.closest('.table-page-card') ?? el.parentElement;
			if (card) this.resizeObserver.observe(card);
			// MutationObserver on the parent card: catches dynamic siblings that appear
			// after async data load (e.g. @if(hk4Enabled) action bars) — these are new
			// DOM nodes that ResizeObserver can't see until after they're inserted.
			const parent = el.parentElement;
			if (parent) {
				this.mutationObserver = new MutationObserver(() => this.computeMaxHeight());
				this.mutationObserver.observe(parent, { childList: true, subtree: false });
			}
			// Observe the page-layout content (catches elements outside the card,
			// e.g. ara-context-detail-cards that load asynchronously and shift the card down)
			const layoutContent = el.closest('ara-page-layout-content');
			if (layoutContent) this.resizeObserver.observe(layoutContent);
			// Observe document root for window resize
			this.resizeObserver.observe(document.documentElement);
		});
	}

	private computeMaxHeight(): void {
		if (!this._enabled) return;
		const el = this.el.nativeElement;
		const top = el.getBoundingClientRect().top;
		if (top === 0) return;

		// araHk4: Wenn die Tabelle in einer .frame-card sitzt, ist diese der Scroll-
		// Container — nutzbare Hoehe geht bis zur Card-Unterkante, nicht bis zum
		// Window-Ende. Fuer vex-theme (kein .frame-card-Ancestor) bleibt das Verhalten
		// window-basiert wie vorher.
		const scrollRoot = el.closest('.frame-card') as HTMLElement | null;
		const bottomBound = scrollRoot ? scrollRoot.getBoundingClientRect().bottom : window.innerHeight;

		const maxHeight = bottomBound - top - this.getBelowSiblingsHeight(el) - this.getFooterHeight() - this.getLayoutBottomPadding(el);
		if (maxHeight > 50) {
			this.renderer.setStyle(el, 'max-height', maxHeight + 'px');
		}
	}

	private getLayoutBottomPadding(el: HTMLElement): number {
		const layout = el.closest('.ara-page-layout') as HTMLElement;
		if (!layout) return 0;
		return parseFloat(getComputedStyle(layout).paddingBottom) || 0;
	}

	private getBelowSiblingsHeight(el: HTMLElement): number {
		const parent = el.parentElement;
		if (!parent) return 0;
		let height = 0;
		let found = false;
		for (const child of Array.from(parent.children)) {
			if (child === el) {
				found = true;
				continue;
			}
			if (found) height += (child as HTMLElement).offsetHeight;
		}
		return height;
	}

	private getFooterHeight(): number {
		const footer = document.querySelector('vex-footer');
		return footer ? (footer as HTMLElement).offsetHeight : 0;
	}

	ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
		this.mutationObserver?.disconnect();
	}
}
